import { useEffect, useState } from "react";

import {
	Button,
	Card,
	TableBody,
	TableCell,
	TableColumn,
	TableHeader,
	TableRow,
	cn,
} from "@heroui/react";
import {
	type ExpandedState,
	flexRender,
	getCoreRowModel,
	getExpandedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import lodash from "lodash";

import { type AppFormApi, useLanguage } from "@onesolution/hooks";

import type { OperationDetailsTabs } from "@onesolution/components/OperationDrawer/OperationDetails";
import Table, {
	baseTableBodyProps,
	baseTableRowProps,
} from "@onesolution/components/Table/Table";
import OperationsFilters from "@onesolution/components/form/OperationsFilters";
import { DEFAULT_OPERATION_STATUS } from "@onesolution/constants";
import { useOperationFilters } from "@onesolution/hooks/useOperationFilters";
import {
	type OperationsList,
	getOperationsBaseFromServer,
} from "@onesolution/serverFn";
import EmptyContent from "./TableEmptyContent";
import { getColumnOperationTable } from "./table.config";

export default function OperationTable({
	onRowClick,
	onCreateOperationClick,
}: {
	onRowClick: (
		clickedOpId: string,
		defaultOpenedTab?: OperationDetailsTabs,
		onlyChat?: boolean,
	) => void;
	onCreateOperationClick?: () => void;
}) {
	const { form, filters } = useOperationFilters("operations-list", {
		status: DEFAULT_OPERATION_STATUS,
	});
	const { t, lang } = useLanguage();
	const {
		data,
		isFetching,
		isError,
		hasNextPage,
		refetch,
		fetchNextPage,
		isFetched,
	} = getOperationsBaseFromServer.useInfiniteQuery(filters);

	useEffect(() => {
		setFullData(data.items);
	}, [data.items]);

	const [fullData, setFullData] = useState<typeof data.items>([]);

	const [expanded, setExpanded] = useState<ExpandedState>({});

	// Table creation
	const table = useReactTable<OperationsList[number]>({
		state: {
			expanded,
			columnPinning: {
				left: ["name"],
			},
		},

		data: fullData,
		enableColumnPinning: true,
		columns: getColumnOperationTable(t, onRowClick, () =>
			setFullData([...fullData]),
		),
		onExpandedChange: setExpanded,
		getCoreRowModel: getCoreRowModel(),
		getRowId: (originalRow) => originalRow.id,
		getExpandedRowModel: getExpandedRowModel(),
		getRowCanExpand: (row) => Boolean(row.original.subitemIds?.length),
		getSubRows: (row) => {
			const trow = row as unknown as { subitems: typeof data.items };

			return trow.subitems ?? [];
		},
	});

	// Show error state
	if (isError) {
		return (
			<div className="flex flex-col justify-center items-center h-64 gap-6">
				<p className="text-danger">{t("operation.load-fail")}</p>

				<Button
					color="primary"
					variant="shadow"
					isLoading={isFetching}
					onPress={() => refetch()}
				>
					{t("basic.retry")}
				</Button>
			</div>
		);
	}

	if (!data) return null;

	return (
		<div className="flex gap-4 flex-col flex-1 min-h-0 ">
			<OperationsFilters noName form={form as AppFormApi} />
			<Card key={JSON.stringify(expanded)}>
				<Table
					hasNextPage={hasNextPage}
					fetchNextPage={fetchNextPage}
					isFetching={isFetching}
					aria-label="List of all the operations"
					key={lang}
					selectionBehavior="replace"
					color="primary"
					classNames={{
						wrapper: "shadow-inner shadow-none overflow-auto ",
						base: "h-full ",
						emptyWrapper: "h-full",
						thead: "pl-6!",
					}}
				>
					<TableHeader columns={table.getHeaderGroups()[0].headers}>
						{(header) => {
							const size = header.column.getSize();
							const isPinned = header.column.getIsPinned();
							const start = header.column.getStart("left");

							return (
								<TableColumn
									isRowHeader
									align={header.id === "assignees" ? "end" : "start"}
									colSpan={header.colSpan}
									key={header.id}
									width={size}
									className={cn("z-0", {
										"sticky z-10": isPinned,
									})}
									style={{
										left: isPinned ? `${start}px` : undefined,
									}}
								>
									{flexRender(
										header.column.columnDef.header,
										header.getContext(),
									)}
								</TableColumn>
							);
						}}
					</TableHeader>

					<TableBody
						key={JSON.stringify(expanded)}
						items={table.getRowModel().rows}
						{...baseTableBodyProps(t)}
						emptyContent={
							<EmptyContent
								onCreateOperationClick={onCreateOperationClick}
								resetFilters={form.resetForm}
							/>
						}
						className=" h-40"
						isLoading={!isFetched}
					>
						{(row) => {
							const rowProps = baseTableRowProps();
							return (
								<TableRow
									key={row.id + lodash.uniqueId() + JSON.stringify(expanded)}
									className={`${rowProps.className} group`}
									onClick={() => row.depth === 0 && onRowClick?.(row.id)}
								>
									{(columnKey) => {
										const cell = row._getAllCellsByColumnId()[columnKey];
										const size = cell.column.getSize();
										const isPinned = cell.column.getIsPinned();
										const start = cell.column.getStart("left");

										return (
											<TableCell
												key={columnKey}
												width={size}
												className={cn(
													"z-0",
													isPinned &&
														`${rowProps.className} group-hover:bg-content3!`,
													{
														"sticky z-10": isPinned,
													},
												)}
												style={{
													left: isPinned ? `${start}px` : undefined,
												}}
											>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext(),
												)}
											</TableCell>
										);
									}}
								</TableRow>
							);
						}}
					</TableBody>
				</Table>
				<div className="min-h-full w-4 bg-background absolute left-0 top-0" />
				<div className="min-w-full h-4 bg-background absolute left-0 top-0 z-0" />
			</Card>
		</div>
	);
}
